import React from "react";
import { Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { Phone, MapPin, Instagram } from "lucide-react";
import { LogoMark, OPAQUE_LOGO_URL } from "./Logo";
import { PUBLIC_PHONE } from "../lib/contact";
import { CATEGORIES, catL10n, catPathFor } from "../lib/categories";
import { BRAND_SLUGS, BRANDS } from "../lib/brands";
import { brandPath, lp } from "../lib/seo";
import { LEGAL_SLUGS, LEGAL_LABELS } from "../lib/legal";

const INSTAGRAM_URL = "https://www.instagram.com/silver_rentacar/";

export default function Footer() {
  const { t, lang } = useApp();
  const fleetCopy = lang === "TR"
    ? { fleet: "Filo", categories: "Kategoriler", brands: "Markalar" }
    : lang === "RU"
      ? { fleet: "Автопарк", categories: "Категории", brands: "Марки" }
      : { fleet: "Fleet", categories: "Categories", brands: "Brands" };

  const scrollToHomeTop = (event) => {
    if (window.location.pathname === lp("/")) {
      event.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <footer id="about" className="border-t border-white/10 bg-[#0E1017] mt-24">
      <div className="max-w-7xl mx-auto grid gap-10 px-5 py-16 sm:px-8 md:grid-cols-2 xl:grid-cols-[1fr_1fr_1.7fr_1fr]">
        <div>
          <Link
            to={lp("/")}
            onClick={scrollToHomeTop}
            data-testid="footer-logo-link"
            aria-label="Silver Drive home"
            className={
              "mb-4 inline-flex rounded-xl bg-white p-2 transition-transform duration-200 " +
              "hover:scale-[1.03]"
            }
          >
            <LogoMark size={60} src={OPAQUE_LOGO_URL} />
          </Link>
          <p className="text-slate-400 text-sm max-w-xs leading-relaxed">{t.footer.tagline}</p>
        </div>
        <div className="space-y-3 text-sm text-slate-300">
          <p data-testid="footer-company-name" className="font-semibold text-teal-100">
            Silver Drive Rent a Car LLC
          </p>
          <div data-testid="footer-address" className="flex items-start gap-3 leading-relaxed">
            <MapPin size={16} className="mt-0.5 shrink-0 text-teal-300" />
            <span>Dukes The Palm Dubai Hotel, Palm Jumeirah Rd - Nakhlat Jumeira - Dubai</span>
          </div>
          <a
            href={`tel:${PUBLIC_PHONE}`}
            data-testid="footer-phone-971564707444"
            className="flex items-center gap-3 transition-colors duration-200 hover:text-teal-200"
          >
            <Phone size={16} className="text-teal-300" />
            {PUBLIC_PHONE}
          </a>
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-instagram-link"
            className="flex items-center gap-3 transition-colors duration-200 hover:text-teal-200"
          >
            <Instagram size={16} className="text-teal-300" />
            @silver_rentacar
          </a>
        </div>
        <nav data-testid="footer-fleet-navigation" aria-label={fleetCopy.fleet}>
          <Link
            to={lp("/#fleet")}
            data-testid="footer-fleet-link"
            className="font-cinzel text-sm uppercase tracking-widest text-teal-100/80 transition-colors hover:text-teal-300"
          >
            {fleetCopy.fleet}
          </Link>
          <div className="mt-4 grid gap-5 sm:grid-cols-2">
            <div>
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                {fleetCopy.categories}
              </p>
              <div className="space-y-1.5">
                {CATEGORIES.map((category) => (
                  <Link
                    key={category.key}
                    to={catPathFor(category, lang)}
                    data-testid={`footer-category-${category.key}`}
                    className="inline-flex min-h-6 items-center py-1.5 text-xs text-slate-300 transition-colors hover:text-teal-300"
                  >
                    {catL10n(category, lang).name}
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                {fleetCopy.brands}
              </p>
              <div className="space-y-1.5">
                {BRAND_SLUGS.map((brandSlug) => (
                  <Link
                    key={brandSlug}
                    to={brandPath(brandSlug)}
                    data-testid={`footer-brand-${brandSlug}`}
                    className="inline-flex min-h-6 items-center py-1.5 text-xs text-slate-300 transition-colors hover:text-teal-300"
                  >
                    {BRANDS[brandSlug].name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </nav>
        <div className="md:text-right">
          <div className="font-cinzel text-teal-100/80 uppercase tracking-widest text-sm mb-3">{t.footer.concierge}</div>
          <p className="text-slate-400 text-sm">{t.footer.deliveryNote}</p>
        </div>
      </div>
      <div className="border-t border-white/5 py-6 text-center text-xs text-slate-400 font-mono-jb">
        <div className="mb-3 flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
          <Link to={lp(`/${LEGAL_SLUGS.rental}`)} data-testid="footer-rental-conditions" className="transition-colors hover:text-teal-300">
            {(LEGAL_LABELS[lang] || LEGAL_LABELS.EN).rental}
          </Link>
          <Link to={lp(`/${LEGAL_SLUGS.privacy}`)} data-testid="footer-privacy-policy" className="transition-colors hover:text-teal-300">
            {(LEGAL_LABELS[lang] || LEGAL_LABELS.EN).privacy}
          </Link>
        </div>
        © {new Date().getFullYear()} Silver Drive Rent a Car. {t.footer.rights}
      </div>
    </footer>
  );
}
